/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
// Core types and utilities
import { ShopCore } from "@/abilities/shop/types/shop-core";

// GraphQL queries and mutations
import {
    addBasketItemMutation,
    basketForUserQuery,
    getArticleShopInfos,
    getBasketId,
} from "../../shop-core/graphql/shop-query";

// Store and composables
import { getWidgetItemFromInfoItem } from "../../shop-core/helper/get-widget-item-from-info-item";
import { useShopStatusStore } from "../../shop-core/store/shop-status.store";
import { useSalesforceAuth } from "./use-salesforce-auth";
import { useSalesforceClient } from "./use-salesforce-client";

// Types
interface SalesforceError {
    networkError?: {
        statusCode?: number;
        response?: {
            status?: number;
        };
    };
}

/**
 * Composable providing Salesforce shop integration functionality
 * @returns Collection of shop-related actions and utilities
 */
export function useSalesforceShop(): ShopCore.IShopActions {
    const { getClient } = useSalesforceClient();
    const { getUserId, refreshToken } = useSalesforceAuth();

    /**
     * Checks if an error is a Salesforce authentication error
     * @param error - The error to check
     * @returns boolean indicating if it's an auth error
     */
    function isSWAuthError(error: unknown): boolean {
        if (error == null) return false;
        const err = error as SalesforceError;
        const networkError = err.networkError;
        const statusCode = networkError?.statusCode || networkError?.response?.status;
        return statusCode !== undefined && statusCode >= 400 && statusCode < 500;
    }

    /**
     * Redirects user to the Salesforce shop with their cart
     * @param cartId - The ID of the user's cart
     */
    async function redirectToBasket(cartId: string | undefined): Promise<void> {
        const store = useShopStatusStore();
        const { shopUrl } = store.salesforceConfig() ?? {};

        if (shopUrl) {
            let redirectUrl = shopUrl;
            // Replace {cartId} if present, otherwise just use the URL as is
            if (shopUrl.includes("{cartId}")) {
                redirectUrl = shopUrl.replace("{cartId}", cartId ?? "");
            }
            window.location.href = redirectUrl;
        }
    }

    /**
     * Adds a part to the user's shopping basket
     * @param articleId - The ID of the article to add
     * @param amountSelected - The quantity to add
     * @returns Promise that resolves when the item is added
     */
    async function addPartToShopBasket(articleId: string, amountSelected: number): Promise<void> {
        const client = await getClient();
        const basketId = getUserId();

        if (!client || !basketId) return;

        try {
            await client.mutate({
                mutation: addBasketItemMutation,
                variables: {
                    BasketId: basketId,
                    Amount: amountSelected,
                    ProductId: articleId,
                },
            });
        } catch (error) {
            if (isSWAuthError(error)) {
                await refreshToken();
                return await addPartToShopBasket(articleId, amountSelected);
            }
            throw error;
        }
    }

    /**
     * Retrieves shop item information for a specific article
     * @param articleId - The ID of the article to get info for
     * @returns Promise with item information or null if not found
     */
    async function getShopItemInfoForArticle(articleId: string): Promise<ShopCore.WidgetItem | null> {
        const client = await getClient();
        if (!client) return null;

        try {
            const { data } = await client.query({
                query: getArticleShopInfos,
                variables: { articleId },
            });

            if (data.article === null) return null;

            return getWidgetItemFromInfoItem({
                price: data.article.price.price ?? null,
                showPriceOnRequest: data.article.showPriceOnRequest ?? false,
                amountAvailable: data.article.stock.stock ?? 0,
                stockStatus: data.article.stock.stockStatus ?? -1,
                currencySign: data.article.price.currency.sign ?? "",
            });
        } catch (error) {
            if (isSWAuthError(error)) {
                await refreshToken();
                return await getShopItemInfoForArticle(articleId);
            }
            return null;
        }
    }

    // Salesforce doesn't support price requests
    async function sendPriceRequestToShop() {
        return Promise.resolve();
    }

    /**
     * Retrieves the current user's basket data
     * @returns Promise with basket data or undefined if not found
     */
    async function getBasket(): Promise<any | undefined> {
        const client = await getClient();
        const basketId = getUserId();

        if (!client || !basketId) return undefined;

        try {
            const { data } = await client.query({
                query: basketForUserQuery,
                variables: { basketId },
                fetchPolicy: "network-only",
            });
            return data;
        } catch (error) {
            if (isSWAuthError(error)) {
                await refreshToken();
                return await getBasket();
            }
            console.error("Failed to fetch basket:", error);
            return undefined;
        }
    }

    /**
     * Retrieves the cart ID for the current user
     * @returns Promise with cart ID or undefined if not found
     */
    async function getCartId(): Promise<string | undefined> {
        const client = await getClient();
        const userId = getUserId();

        if (!client || !userId) return undefined;

        try {
            const { data } = await client.query({
                query: getBasketId,
                variables: { userId },
                fetchPolicy: "no-cache",
            });
            return data.currentBasketIdFromUserId.basketId;
        } catch (error) {
            if (isSWAuthError(error)) {
                await refreshToken();
                return await getCartId();
            }
            return undefined;
        }
    }

    // Removed duplicate implementation of isSWAuthError as it's now defined at the top

    /**
     * Returns the Salesforce shop client instance
     * @returns Promise with the shop client
     */
    async function getShopClient() {
        return await getClient();
    }

    /**
     * Gets the basket ID for the current user
     * @returns The current user's basket ID
     */
    function getBasketIdForCurrentUser(): string | undefined {
        return getUserId();
    }

    // Return the public API
    return {
        // Core shopping functionality
        addPartToShopBasket,
        getShopItemInfoForArticle,
        sendPriceRequestToShop,

        // Basket operations
        getBasket,
        getCartId,
        getBasketIdForCurrentUser,

        // Navigation and client access
        redirectToBasket,
        getShopClient,
    };
}
